import { Camera, ExternalLink, MessageSquare } from 'lucide-react';
import type { AgentDetail } from '@command-room/shared';
import { STATUS_STYLES } from '@/components/agent-card/AgentCard';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/cn';
import { relativeTime } from '@/lib/format';
import { initialsOf } from '@/lib/initials';
import type { AgentProfile } from '@/lib/agentProfile';

type StatusKey = keyof typeof STATUS_STYLES;

interface ProfileHeaderProps {
  data: AgentDetail;
  profile: AgentProfile;
  isAI: boolean;
  isAdmin: boolean;
  isActive: boolean;
  togglePending: boolean;
  onToggleActive: (enabled: boolean) => void;
  onEditAvatar: () => void;
}

function statusKey(data: AgentDetail, profile: AgentProfile): StatusKey {
  if (data.status === 'inactive') return 'inactive';
  if (profile.working?.live) return 'busy';
  if (data.status === 'onboarding') return 'onboarding';
  return 'active';
}

const STATUS_LABEL: Record<StatusKey, string> = {
  active: 'Available',
  busy: 'Working now',
  scheduled: 'Scheduled',
  onboarding: 'Onboarding',
  inactive: 'Disabled',
};

export function ProfileHeader({
  data,
  profile,
  isAI,
  isAdmin,
  isActive,
  togglePending,
  onToggleActive,
  onEditAvatar,
}: ProfileHeaderProps) {
  const status = statusKey(data, profile);
  return (
    <header className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
      <div
        className="h-28 bg-gradient-to-br from-primary/40 via-primary/15 to-accent sm:h-36"
        aria-hidden="true"
      />
      <div className="px-5 pb-5 sm:px-8">
        <div className="-mt-12 flex items-end justify-between gap-4 sm:-mt-14">
          <button
            type="button"
            className="group relative shrink-0 rounded-full"
            onClick={onEditAvatar}
            title="Change portrait"
          >
            <Avatar className="h-24 w-24 ring-4 ring-card sm:h-28 sm:w-28">
              {data.agent_icon_url && <AvatarImage src={data.agent_icon_url} alt={data.name} />}
              <AvatarFallback className="text-2xl">{initialsOf(data.name)}</AvatarFallback>
            </Avatar>
            <span className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
              <Camera className="h-5 w-5 text-white" />
            </span>
          </button>
          {isAdmin && (
            <label className="mb-1 flex cursor-pointer select-none items-center gap-2 text-sm text-muted-foreground">
              <Switch checked={isActive} onCheckedChange={onToggleActive} disabled={togglePending} />
              {isActive ? (isAI ? 'Enabled' : 'Active') : isAI ? 'Disabled' : 'Inactive'}
            </label>
          )}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2">
          <h1 className="text-3xl font-semibold tracking-tight">{data.name}</h1>
          <span
            className={cn(
              'rounded-full border px-2.5 py-0.5 text-[11px] font-medium uppercase tracking-wider',
              STATUS_STYLES[status],
            )}
          >
            {STATUS_LABEL[status]}
          </span>
        </div>

        {profile.headline && (
          <p className="mt-1 max-w-3xl text-base text-foreground/80">{profile.headline}</p>
        )}

        <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <Badge variant={isAI ? 'secondary' : 'outline'}>{data.type ?? 'unknown'}</Badge>
          <span>{data.role ?? 'No role'}</span>
          <span aria-hidden="true">·</span>
          <span>{data.department ?? 'No department'}</span>
          {data.slack_handle && (
            <>
              <span aria-hidden="true">·</span>
              <span className="inline-flex items-center gap-1">
                <MessageSquare className="h-3.5 w-3.5" /> {data.slack_handle}
              </span>
            </>
          )}
          {data.last_seen_at && (
            <>
              <span aria-hidden="true">·</span>
              <span>Last seen {relativeTime(data.last_seen_at)}</span>
            </>
          )}
          {isAI && data.n8n_workflow_url && (
            <a
              href={data.n8n_workflow_url}
              target="_blank"
              rel="noreferrer"
              className="ml-auto inline-flex items-center gap-1 text-primary hover:underline"
            >
              Open n8n workflow <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
        </div>
      </div>
    </header>
  );
}
